import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ComponentCard from '../../components/common/ComponentCard';
import SearchableSelect, {
  type SearchableSelectOption,
} from '../../components/common/SearchableSelect';
import Label from '../../components/form/Label';
import Input from '../../components/form/input/InputField';
import { useAuth } from '../../context/AuthContext';
import { mergePrefill } from '../../types/onboardingIntake';
import { api, apiEndpoints } from '../../utils/apiService';
import OnboardingIntakePrefillControl from './OnboardingIntakePrefillControl';

type TwilioSecondaryProfileStatus =
  | 'not_created'
  | 'draft'
  | 'pending-review'
  | 'in-review'
  | 'twilio-approved'
  | 'twilio-rejected';

type TwilioSecondaryProfileFormData = {
  friendly_name: string;
  customer_name: string;
  notification_email: string;
  business_name: string;
  website_url: string;
  business_street_address: string;
  business_street_address2: string;
  business_city: string;
  business_state_province_region: string;
  business_postal_code: string;
  business_country: string;
  business_contact_email: string;
  business_contact_phone: string;
  business_registration_number: string;
  business_registration_identifier: string;
  business_identity: string;
  business_type: string;
  business_industry: string;
  business_regions_of_operation: string;
  authorized_representative_first_name: string;
  authorized_representative_last_name: string;
  authorized_representative_email: string;
  authorized_representative_phone_number: string;
  authorized_representative_business_title: string;
  authorized_representative_job_position: string;
};

type TwilioSecondaryProfileRecord = {
  id?: number;
  store?: number;
  profile_sid?: string;
  friendly_name?: string;
  customer_name?: string;
  notification_email?: string;
  status?: TwilioSecondaryProfileStatus;
  form_data?: Partial<TwilioSecondaryProfileFormData>;
  component_sids?: Record<string, unknown>;
  twilio_payload?: Record<string, unknown>;
  missing_fields?: string[];
  last_synced_at?: string;
};

type TwilioSecondaryProfileResponse = {
  exists: boolean;
  status: TwilioSecondaryProfileStatus;
  profile_sid: string;
  profile: TwilioSecondaryProfileRecord | null;
  autofill: Partial<TwilioSecondaryProfileFormData>;
  missing_fields: string[];
};

type TwilioProfileEvaluation = {
  sid?: string;
  status?: string;
  results?: Array<{
    failure_reason?: string;
  }>;
};

type FieldConfig = {
  name: keyof TwilioSecondaryProfileFormData;
  label: string;
  type?: string;
  required?: boolean;
  placeholder?: string;
};

interface TwilioSecondaryProfilesTabProps {
  storeOptions: SearchableSelectOption[];
}

const emptySecondaryProfileForm: TwilioSecondaryProfileFormData = {
  friendly_name: '',
  customer_name: '',
  notification_email: '',
  business_name: '',
  website_url: '',
  business_street_address: '',
  business_street_address2: '',
  business_city: '',
  business_state_province_region: '',
  business_postal_code: '',
  business_country: 'US',
  business_contact_email: '',
  business_contact_phone: '',
  business_registration_number: '',
  business_registration_identifier: '',
  business_identity: '',
  business_type: '',
  business_industry: 'FOOD_AND_BEVERAGE',
  business_regions_of_operation: '',
  authorized_representative_first_name: '',
  authorized_representative_last_name: '',
  authorized_representative_email: '',
  authorized_representative_phone_number: '',
  authorized_representative_business_title: '',
  authorized_representative_job_position: 'VP',
};

const statusLabels: Record<TwilioSecondaryProfileStatus, string> = {
  not_created: 'Not Created',
  draft: 'Draft',
  'pending-review': 'Pending Review',
  'in-review': 'In Review',
  'twilio-approved': 'Twilio Approved',
  'twilio-rejected': 'Twilio Rejected',
};

const regionOptions = [
  { value: 'AFRICA', label: 'Africa' },
  { value: 'ASIA', label: 'Asia' },
  { value: 'AUSTRALIA', label: 'Australia' },
  { value: 'EUROPE', label: 'Europe' },
  { value: 'LATIN_AMERICA', label: 'Latin America' },
  { value: 'USA_AND_CANADA', label: 'USA and Canada' },
];

const businessIdentityOptions = [
  { value: 'direct_customer', label: 'direct_customer' },
  { value: 'isv_reseller_or_partner', label: 'isv_reseller_or_partner' },
  { value: 'unknown', label: 'unknown' },
];

const businessTypeOptions = [
  { value: 'Sole Proprietorship', label: 'Sole Proprietorship' },
  { value: 'Partnership', label: 'Partnership' },
  {
    value: 'Limited Liability Corporation',
    label: 'Limited Liability Corporation',
  },
  { value: 'Co-operative', label: 'Co-operative' },
  { value: 'Non-profit Corporation', label: 'Non-profit Corporation' },
  { value: 'Corporation', label: 'Corporation' },
];

const businessRegistrationIdentifierOptions = [
  { value: 'EIN', label: 'EIN (US)' },
  { value: 'CBN', label: 'CBN (CA)' },
  { value: 'CN', label: 'CN (UK)' },
  { value: 'ACN', label: 'ACN (AU)' },
  { value: 'CIN', label: 'CIN (IN)' },
  { value: 'VAT', label: 'VAT (EU)' },
  { value: 'VATRN', label: 'VATRN (RO)' },
  { value: 'RN', label: 'RN (IS)' },
  { value: 'Other', label: 'Other' },
];

const businessIndustryOptions = [
  'AGRICULTURE',
  'AUTOMOTIVE',
  'BANKING',
  'CONSUMER',
  'EDUCATION',
  'ELECTRONICS',
  'ENERGY',
  'ENGINEERING',
  'FAST_MOVING_CONSUMER_GOODS',
  'FINANCIAL',
  'FINTECH',
  'FOOD_AND_BEVERAGE',
  'GOVERNMENT',
  'HEALTHCARE',
  'HOSPITALITY',
  'INSURANCE',
  'JEWELRY',
  'LEGAL',
  'MANUFACTURING',
  'MEDIA',
  'NOT_FOR_PROFIT',
  'OIL_AND_GAS',
  'ONLINE',
  'RAW_MATERIALS',
  'REAL_ESTATE',
  'RELIGION',
  'RETAIL',
  'TECHNOLOGY',
  'TELECOMMUNICATIONS',
  'TRANSPORTATION',
  'TRAVEL',
].map((value) => ({ value, label: value }));

const jobPositionOptions = [
  'Director',
  'GM',
  'VP',
  'CEO',
  'CFO',
  'General Counsel',
  'Other',
].map((value) => ({ value, label: value }));

const selectOptionsByField: Partial<
  Record<
    keyof TwilioSecondaryProfileFormData,
    Array<{ value: string; label: string }>
  >
> = {
  business_identity: businessIdentityOptions,
  business_registration_identifier: businessRegistrationIdentifierOptions,
  business_type: businessTypeOptions,
  business_industry: businessIndustryOptions,
  authorized_representative_job_position: jobPositionOptions,
};

const businessFields: FieldConfig[] = [
  { name: 'friendly_name', label: 'Friendly Name', required: true },
  { name: 'customer_name', label: 'Customer Name' },
  {
    name: 'notification_email',
    label: 'Notification Email',
    type: 'email',
    required: true,
  },
  { name: 'business_name', label: 'Business Name' },
  {
    name: 'website_url',
    label: 'Website URL',
    placeholder: 'https://example.com',
  },
  { name: 'business_street_address', label: 'Street Address' },
  { name: 'business_street_address2', label: 'Street Address 2' },
  { name: 'business_city', label: 'City' },
  {
    name: 'business_state_province_region',
    label: 'State / Province / Region',
  },
  { name: 'business_postal_code', label: 'Postal Code' },
  { name: 'business_country', label: 'Country' },
  {
    name: 'business_contact_email',
    label: 'Business Contact Email',
    type: 'email',
  },
  { name: 'business_contact_phone', label: 'Business Contact Phone' },
  {
    name: 'business_registration_number',
    label: 'Business Registration Number',
  },
  {
    name: 'business_registration_identifier',
    label: 'Business Registration ID Type',
    required: true,
  },
  { name: 'business_identity', label: 'Business Identity', required: true },
  { name: 'business_type', label: 'Business Type', required: true },
  {
    name: 'business_industry',
    label: 'Business Industry',
    required: true,
  },
  {
    name: 'business_regions_of_operation',
    label: 'Regions of Operation',
  },
];

const representativeFields: FieldConfig[] = [
  { name: 'authorized_representative_first_name', label: 'First Name' },
  { name: 'authorized_representative_last_name', label: 'Last Name' },
  { name: 'authorized_representative_email', label: 'Email', type: 'email' },
  { name: 'authorized_representative_phone_number', label: 'Phone Number' },
  { name: 'authorized_representative_business_title', label: 'Business Title' },
  { name: 'authorized_representative_job_position', label: 'Job Position' },
];

const readSelectedStoreId = () => {
  if (typeof window === 'undefined') return '';
  return localStorage.getItem('currentStoreId') || '';
};

const textValue = (value: unknown) => (value == null ? '' : String(value));

const normalizeRegionValue = (value: string) =>
  value.trim().toUpperCase().replace(/\s+/g, '_').replace(/&/g, 'AND');

const getResponseError = async (response: Response, fallback: string) => {
  const contentType = response.headers.get('content-type');
  if (contentType?.includes('application/json')) {
    const data = await response.json().catch(() => ({}));
    return data.detail || data.message || data.error || fallback;
  }
  return fallback;
};

const statusBadgeClassName = (status: TwilioSecondaryProfileStatus) => {
  switch (status) {
    case 'twilio-approved':
      return 'bg-success-50 text-success-700 ring-success-200 dark:bg-success-900/20 dark:text-success-400 dark:ring-success-800';
    case 'twilio-rejected':
      return 'bg-error-50 text-error-700 ring-error-200 dark:bg-error-900/20 dark:text-error-400 dark:ring-error-800';
    case 'pending-review':
    case 'in-review':
      return 'bg-warning-50 text-warning-700 ring-warning-200 dark:bg-warning-900/20 dark:text-warning-400 dark:ring-warning-800';
    case 'draft':
      return 'bg-blue-50 text-blue-700 ring-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:ring-blue-800';
    default:
      return 'bg-gray-100 text-gray-700 ring-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700';
  }
};

const normalizeFormData = (
  state: TwilioSecondaryProfileResponse,
): TwilioSecondaryProfileFormData => {
  const profile = state.profile;
  const merged = {
    ...emptySecondaryProfileForm,
    ...state.autofill,
    ...(profile?.form_data || {}),
    friendly_name:
      profile?.friendly_name || state.autofill?.friendly_name || '',
    customer_name:
      profile?.customer_name || state.autofill?.customer_name || '',
    notification_email:
      profile?.notification_email || state.autofill?.notification_email || '',
    authorized_representative_job_position:
      profile?.form_data?.authorized_representative_job_position ||
      state.autofill?.authorized_representative_job_position ||
      emptySecondaryProfileForm.authorized_representative_job_position,
  };

  return Object.keys(emptySecondaryProfileForm).reduce(
    (acc, key) => ({
      ...acc,
      [key]: textValue(merged[key as keyof TwilioSecondaryProfileFormData]),
    }),
    { ...emptySecondaryProfileForm },
  );
};

const TwilioSecondaryProfilesTab: React.FC<TwilioSecondaryProfilesTabProps> = ({
  storeOptions,
}) => {
  const { user } = useAuth();
  const [selectedStoreId, setSelectedStoreId] = useState(readSelectedStoreId);
  const [profileState, setProfileState] =
    useState<TwilioSecondaryProfileResponse | null>(null);
  const [formData, setFormData] = useState<TwilioSecondaryProfileFormData>(
    emptySecondaryProfileForm,
  );
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<
    'create' | 'sync' | 'submit' | null
  >(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const selectedStoreLabel = useMemo(
    () =>
      storeOptions.find((option) => String(option.value) === selectedStoreId)
        ?.label || (selectedStoreId ? `Store ID ${selectedStoreId}` : ''),
    [selectedStoreId, storeOptions],
  );

  const applyProfileState = useCallback(
    (state: TwilioSecondaryProfileResponse) => {
      setProfileState(state);
      setFormData(normalizeFormData(state));
    },
    [],
  );

  const fetchProfileState = useCallback(async () => {
    if (!user?.is_platform_admin || !selectedStoreId) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.get(apiEndpoints.getTwilioSecondaryProfile());
      if (response?.ok) {
        applyProfileState(await response.json());
      } else if (response) {
        setError(
          await getResponseError(
            response,
            `Failed to load secondary profile (Status: ${response.status})`,
          ),
        );
      } else {
        setError('Failed to load secondary profile: No response from server');
      }
    } catch (err) {
      console.error('Twilio secondary profile load error:', err);
      setError('An unexpected error occurred while loading the profile.');
    } finally {
      setLoading(false);
    }
  }, [applyProfileState, selectedStoreId, user?.is_platform_admin]);

  useEffect(() => {
    fetchProfileState();
  }, [fetchProfileState]);

  const handleStoreChange = (value: string) => {
    setSelectedStoreId(value);
    setProfileState(null);
    setError('');
    setSuccess('');

    if (typeof window === 'undefined') return;
    if (value) {
      localStorage.setItem('currentStoreId', value);
    } else {
      localStorage.removeItem('currentStoreId');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const selectedRegions = useMemo(
    () =>
      formData.business_regions_of_operation
        .split(',')
        .map(normalizeRegionValue)
        .filter(Boolean),
    [formData.business_regions_of_operation],
  );

  const handleRegionToggle = (region: string) => {
    setFormData((prev) => {
      const currentRegions = prev.business_regions_of_operation
        .split(',')
        .map(normalizeRegionValue)
        .filter(Boolean);
      const nextRegions = currentRegions.includes(region)
        ? currentRegions.filter((value) => value !== region)
        : [...currentRegions, region];

      return {
        ...prev,
        business_regions_of_operation: nextRegions.join(','),
      };
    });
  };

  const handleIntakePrefill = (
    prefill: Record<string, any>,
    overwrite: boolean,
  ) => {
    setFormData((prev) => mergePrefill(prev as any, prefill as any, overwrite));
    setSuccess('Secondary profile fields populated from onboarding intake.');
  };

  const buildPayload = () => {
    const payload = { ...formData };
    (
      Object.keys(payload) as Array<keyof TwilioSecondaryProfileFormData>
    ).forEach((key) => {
      payload[key] = payload[key].trim();
    });
    return payload;
  };

  const createProfile = async () => {
    setError('');
    setSuccess('');

    if (!formData.friendly_name.trim() || !formData.notification_email.trim()) {
      setError('Friendly Name and Notification Email are required.');
      return;
    }

    if (
      !formData.business_identity.trim() ||
      !formData.business_registration_identifier.trim() ||
      !formData.business_type.trim() ||
      !formData.business_industry.trim()
    ) {
      setError(
        'Business Identity, Business Registration ID Type, Business Type, and Business Industry are required.',
      );
      return;
    }

    setActionLoading('create');
    try {
      const response = await api.post(
        apiEndpoints.createTwilioSecondaryProfile(),
        buildPayload(),
      );
      if (response?.ok) {
        const state = (await response.json()) as TwilioSecondaryProfileResponse;
        applyProfileState(state);
        const evaluation = state.profile?.twilio_payload?.evaluation as
          | TwilioProfileEvaluation
          | undefined;
        setSuccess(
          evaluation?.status === 'compliant'
            ? 'Secondary profile created and passed Twilio evaluation.'
            : `Secondary profile draft saved. Twilio evaluation status: ${
                evaluation?.status || 'unknown'
              }.`,
        );
      } else if (response) {
        const data = await response.json().catch(() => null);
        if (data?.exists) {
          applyProfileState(data as TwilioSecondaryProfileResponse);
        }
        setError(
          data?.detail ||
            `Failed to create secondary profile (Status: ${response.status})`,
        );
      } else {
        setError('Failed to create secondary profile: No response from server');
      }
    } catch (err) {
      console.error('Twilio secondary profile create error:', err);
      setError('An unexpected error occurred while creating the profile.');
    } finally {
      setActionLoading(null);
    }
  };

  const syncProfile = async () => {
    setActionLoading('sync');
    setError('');
    setSuccess('');

    try {
      const response = await api.post(
        apiEndpoints.syncTwilioSecondaryProfile(),
      );
      if (response?.ok) {
        applyProfileState(await response.json());
        setSuccess('Secondary customer profile status synced.');
      } else if (response) {
        setError(
          await getResponseError(
            response,
            `Failed to sync secondary profile (Status: ${response.status})`,
          ),
        );
      } else {
        setError('Failed to sync secondary profile: No response from server');
      }
    } catch (err) {
      console.error('Twilio secondary profile sync error:', err);
      setError('An unexpected error occurred while syncing the profile.');
    } finally {
      setActionLoading(null);
    }
  };

  const submitProfile = async () => {
    setActionLoading('submit');
    setError('');
    setSuccess('');

    try {
      const response = await api.post(
        apiEndpoints.submitTwilioSecondaryProfile(),
      );
      if (response?.ok) {
        applyProfileState(await response.json());
        setSuccess('Secondary customer profile submitted for review.');
      } else if (response) {
        setError(
          await getResponseError(
            response,
            `Failed to submit secondary profile (Status: ${response.status})`,
          ),
        );
      } else {
        setError('Failed to submit secondary profile: No response from server');
      }
    } catch (err) {
      console.error('Twilio secondary profile submit error:', err);
      setError('An unexpected error occurred while submitting the profile.');
    } finally {
      setActionLoading(null);
    }
  };

  const renderField = (field: FieldConfig) => {
    const selectOptions = selectOptionsByField[field.name];

    if (field.name === 'business_regions_of_operation') {
      return (
        <div key={field.name} className="md:col-span-2 xl:col-span-3">
          <Label>{field.label}</Label>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {regionOptions.map((region) => {
              const checked = selectedRegions.includes(region.value);
              return (
                <label
                  key={region.value}
                  className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-sm font-medium transition ${
                    checked
                      ? 'border-brand-300 bg-brand-50 text-brand-700 dark:border-brand-500/40 dark:bg-brand-500/10 dark:text-brand-300'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-brand-200 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 dark:hover:border-brand-500/30'
                  } ${
                    loading || actionLoading
                      ? 'cursor-not-allowed opacity-60'
                      : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => handleRegionToggle(region.value)}
                    disabled={loading || !!actionLoading}
                    className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800"
                  />
                  <span>{region.label}</span>
                </label>
              );
            })}
          </div>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Stored as Twilio region codes:{' '}
            {selectedRegions.join(', ') || 'none'}
          </p>
        </div>
      );
    }

    if (selectOptions) {
      return (
        <div key={field.name}>
          <Label>
            {field.label}
            {field.required ? <span className="text-error-500"> *</span> : null}
          </Label>
          <select
            name={field.name}
            value={formData[field.name]}
            onChange={handleSelectChange}
            required={field.required}
            disabled={loading || !!actionLoading}
            className="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-none focus:ring focus:ring-brand-500/10 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:focus:border-brand-800"
          >
            <option value="">Select {field.label}</option>
            {selectOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      );
    }

    return (
      <div key={field.name}>
        <Label>
          {field.label}
          {field.required ? <span className="text-error-500"> *</span> : null}
        </Label>
        <Input
          type={field.type || 'text'}
          name={field.name}
          value={formData[field.name]}
          onChange={handleInputChange}
          placeholder={field.placeholder}
          required={field.required}
          disabled={loading || !!actionLoading}
        />
      </div>
    );
  };

  if (!user?.is_platform_admin) {
    return (
      <ComponentCard
        title="Secondary Customer Profiles"
        className="overflow-hidden"
      >
        <div className="p-4 text-sm text-error-500">
          This tool is only available to platform admins.
        </div>
      </ComponentCard>
    );
  }

  const status = profileState?.status || 'not_created';
  const evaluation = profileState?.profile?.twilio_payload?.evaluation as
    | TwilioProfileEvaluation
    | undefined;
  const provisioningError = profileState?.profile?.twilio_payload
    ?.provisioning_error as string | undefined;
  const canCreate =
    !!selectedStoreId &&
    (!profileState?.exists || profileState.status === 'draft');
  const canSubmit =
    !!profileState?.exists &&
    !!profileState.profile_sid &&
    profileState.status === 'draft' &&
    evaluation?.status === 'compliant';

  return (
    <div className="space-y-6">
      <ComponentCard
        title="Secondary Customer Profile Store"
        className="overflow-hidden"
      >
        <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
          <div>
            <Label>Selected Store</Label>
            <SearchableSelect
              options={storeOptions}
              value={selectedStoreId}
              onChange={handleStoreChange}
              placeholder="Select a store"
              required
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Requests use the selected store as the X-Store-ID header.
            </p>
          </div>
          <button
            type="button"
            onClick={fetchProfileState}
            disabled={!selectedStoreId || loading || !!actionLoading}
            className="inline-flex items-center justify-center rounded-lg bg-gray-100 px-4 py-3 text-sm font-semibold text-gray-700 transition hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            {loading ? 'Loading...' : 'Reload'}
          </button>
        </div>
      </ComponentCard>

      {!selectedStoreId ? (
        <div className="rounded-lg border border-warning-200 bg-warning-50 p-4 text-sm text-warning-700 dark:border-warning-800 dark:bg-warning-900/20 dark:text-warning-400">
          Select a store before loading or creating a Twilio secondary profile.
        </div>
      ) : null}

      <OnboardingIntakePrefillControl
        target="secondary_profile"
        disabled={!selectedStoreId || loading || !!actionLoading}
        onApply={handleIntakePrefill}
      />

      <ComponentCard title="Profile Status" className="overflow-hidden">
        <div className="grid gap-4 md:grid-cols-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Store
            </p>
            <p className="mt-1 text-sm font-semibold text-gray-800 dark:text-white/90">
              {selectedStoreLabel || 'No store selected'}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Status
            </p>
            <span
              className={`mt-1 inline-flex rounded-full px-3 py-1 text-xs font-semibold ring-1 ${statusBadgeClassName(
                status,
              )}`}
            >
              {statusLabels[status]}
            </span>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Profile SID
            </p>
            <p className="mt-1 break-all text-sm font-semibold text-gray-800 dark:text-white/90">
              {profileState?.profile_sid || 'Not created'}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Last Synced
            </p>
            <p className="mt-1 text-sm font-semibold text-gray-800 dark:text-white/90">
              {profileState?.profile?.last_synced_at
                ? new Date(profileState.profile.last_synced_at).toLocaleString()
                : 'Never'}
            </p>
          </div>
        </div>

        {profileState?.missing_fields?.length ? (
          <div className="mt-5 rounded-lg border border-warning-200 bg-warning-50 p-4 text-sm text-warning-700 dark:border-warning-800 dark:bg-warning-900/20 dark:text-warning-400">
            Missing fields: {profileState.missing_fields.join(', ')}
          </div>
        ) : null}

        {evaluation ? (
          <div
            className={`mt-5 rounded-lg border p-4 text-sm ${
              evaluation.status === 'compliant'
                ? 'border-success-200 bg-success-50 text-success-700 dark:border-success-800 dark:bg-success-900/20 dark:text-success-400'
                : 'border-warning-200 bg-warning-50 text-warning-700 dark:border-warning-800 dark:bg-warning-900/20 dark:text-warning-400'
            }`}
          >
            Twilio evaluation: {evaluation.status || 'unknown'}
            {evaluation.sid ? ` (${evaluation.sid})` : ''}
          </div>
        ) : null}

        {provisioningError ? (
          <div className="mt-5 rounded-lg border border-error-200 bg-error-50 p-4 text-sm text-error-600 dark:border-error-800 dark:bg-error-900/20 dark:text-error-400">
            Last provisioning error: {provisioningError}
          </div>
        ) : null}

        {error ? (
          <div className="mt-5 rounded-lg border border-error-200 bg-error-50 p-4 text-sm text-error-500 dark:border-error-800 dark:bg-error-900/20">
            {error}
          </div>
        ) : null}

        {success ? (
          <div className="mt-5 rounded-lg border border-success-200 bg-success-50 p-4 text-sm text-success-600 dark:border-success-800 dark:bg-success-900/20 dark:text-success-400">
            {success}
          </div>
        ) : null}
      </ComponentCard>

      <ComponentCard
        title="Business and Profile Fields"
        className="overflow-hidden"
      >
        <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-300">
          Autofill values come from the selected store. You can override fields
          before creating the Twilio draft profile.
        </div>
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {businessFields.map(renderField)}
        </div>
      </ComponentCard>

      <ComponentCard
        title="Authorized Representative"
        className="overflow-hidden"
      >
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {representativeFields.map(renderField)}
        </div>
      </ComponentCard>

      <div className="flex flex-wrap justify-end gap-3">
        {!profileState?.exists || profileState.status === 'draft' ? (
          <button
            type="button"
            onClick={createProfile}
            disabled={!canCreate || loading || !!actionLoading}
            className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-5 py-3 text-sm font-semibold text-white shadow-theme-xs transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {actionLoading === 'create'
              ? 'Creating...'
              : profileState?.exists
                ? 'Retry Provisioning & Evaluation'
                : 'Create & Evaluate Secondary Profile'}
          </button>
        ) : (
          <button
            type="button"
            onClick={syncProfile}
            disabled={!selectedStoreId || loading || !!actionLoading}
            className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-5 py-3 text-sm font-semibold text-white shadow-theme-xs transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {actionLoading === 'sync' ? 'Syncing...' : 'Sync Status'}
          </button>
        )}

        {profileState?.exists ? (
          <button
            type="button"
            onClick={submitProfile}
            disabled={!canSubmit || loading || !!actionLoading}
            className="inline-flex items-center justify-center rounded-lg bg-success-500 px-5 py-3 text-sm font-semibold text-white shadow-theme-xs transition hover:bg-success-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {actionLoading === 'submit' ? 'Submitting...' : 'Submit for Review'}
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default TwilioSecondaryProfilesTab;
